import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { 
  Brain, 
  Code, 
  Users, 
  BarChart3, 
  ArrowRight, 
  CheckCircle,
  Zap,
  Shield,
  Clock,
  Target,
  Star,
  Crown,
  Building2
} from 'lucide-react'
import { BUILD_VERSION } from '@/utils/buildVersion'

export default function HomePage() {
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null)

  // Check if user is already logged in and redirect to dashboard
  useEffect(() => {
    const token = localStorage.getItem('token')
    if (token) {
      // User is logged in, redirect to dashboard
      window.location.href = '/recruiter/dashboard'
    }
  }, [])

  const features = [
    {
      icon: <Code className="w-8 h-8" />,
      title: "Multi-Language Coding Sandbox",
      description: "Browser-based coding environment with language selection, configurable AI assistance, and real-world debugging challenges",
      stats: "Supports 15+ programming languages"
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Pair Programming & Whiteboard",
      description: "Real-time collaborative coding and whiteboard discussions with AI bot assistance",
      stats: "Simulates actual team collaboration"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "System Design Simulator",
      description: "Interactive drag-and-drop interface for system architecture evaluation",
      stats: "Covers 50+ system design patterns"
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Video Interview with AI Analysis",
      description: "Resume-based question generation with confidence scoring and discussion summaries",
      stats: "Analyzes confidence, language, and answers"
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Comprehensive Recruiter Dashboard",
      description: "Position tracking, candidate comparison, custom questions, and detailed analytics",
      stats: "Compare candidates across positions"
    }
  ]

  const benefits = [
    { icon: <Zap className="w-5 h-5" />, text: "10x faster than traditional interviews" },
    { icon: <Shield className="w-5 h-5" />, text: "Eliminates unconscious bias" },
    { icon: <Clock className="w-5 h-5" />, text: "24/7 availability" },
    { icon: <CheckCircle className="w-5 h-5" />, text: "90% candidate satisfaction" }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Brain className="w-8 h-8 text-primary-600" />
              <span className="text-xl font-bold text-gray-900">AI Recruiter</span>
            </div>
            <nav className="hidden md:flex space-x-8">
              <Link to="/jobs" className="text-gray-600 hover:text-primary-600 transition-colors">
                Browse Jobs
              </Link>
              <Link to="/candidate/dashboard" className="text-gray-600 hover:text-primary-600 transition-colors">
                My Applications
              </Link>
              <a href="#features" className="text-gray-600 hover:text-primary-600 transition-colors">
                Features
              </a>
              <a href="#demo" className="text-gray-600 hover:text-primary-600 transition-colors">
                Demo
              </a>
              <a href="#pricing" className="text-gray-600 hover:text-primary-600 transition-colors">
                Pricing
              </a>
            </nav>
            <div className="flex space-x-4">
              <Link to="/login" className="btn-secondary">
                Login
              </Link>
              <Link to="/login?returnUrl=/interview" className="btn-primary">
                Start Interview
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 pb-32 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              Open-Book Interview
              <span className="text-primary-600"> Platform</span>
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-2xl text-primary-600 font-semibold mb-4"
            >
              "Hire for skills, not memory"
            </motion.div>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto"
            >
              Revolutionary interview platform that evaluates candidates through real-world scenarios, 
              pair programming, whiteboard discussions, and comprehensive technical assessments with configurable AI assistance.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
            >
              <Link to="/login?returnUrl=/interview" className="btn-primary text-lg px-8 py-3 inline-flex items-center">
                Start Free Interview
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/jobs" className="btn-secondary text-lg px-8 py-3 inline-flex items-center">
                Browse Jobs
                <Users className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/demo" className="btn-secondary text-lg px-8 py-3">
                Watch Demo
              </Link>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="flex flex-wrap justify-center gap-6"
            >
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center space-x-2 text-gray-600">
                  <div className="text-success-600">{benefit.icon}</div>
                  <span className="text-sm">{benefit.text}</span>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Complete Interview Platform
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Everything you need to assess technical talent with AI-powered precision
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onHoverStart={() => setHoveredFeature(index)}
                onHoverEnd={() => setHoveredFeature(null)}
                className="card hover:shadow-custom-lg transition-all duration-300 cursor-pointer"
              >
                <div className={`text-primary-600 mb-4 transition-transform duration-300 ${
                  hoveredFeature === index ? 'scale-110' : ''
                }`}>
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 mb-4">
                  {feature.description}
                </p>
                <div className="stats-badge info">
                  {feature.stats}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Choose Your Perfect Plan
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Transform your hiring process with AI-powered interviews. Find the perfect plan for your team size and needs.
            </p>
          </div>

          {/* Coupon Section */}
          <div className="max-w-md mx-auto mb-12 px-4">
            <div className="bg-white/90 backdrop-blur-sm border border-yellow-200 rounded-lg shadow-sm p-6">
              <div className="text-center pb-3 border-b border-gray-200 mb-4">
                <h3 className="text-lg font-semibold text-yellow-700 flex items-center justify-center gap-2">
                  <Star className="w-5 h-5" />
                  Special Offer Available!
                </h3>
                <p className="text-sm text-gray-600 mt-1">Use code "hirehubble" for 20% off</p>
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Starter Plan */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 hover:shadow-lg transition-shadow">
              <div className="text-center">
                <Zap className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Starter</h3>
                <p className="text-gray-600 mt-2 mb-4">Perfect for small teams</p>
                <div className="text-4xl font-bold text-gray-900 mb-2">$49<span className="text-lg font-medium text-gray-600">/month</span></div>
                <ul className="text-sm text-gray-700 space-y-2 mb-6">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />10 interviews/month</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Basic AI assessment</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Email support</li>
                </ul>
                <button 
                  onClick={() => {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      window.location.href = `/login?returnUrl=${encodeURIComponent('/checkout?planId=1&planName=Starter&price=49')}`;
                    } else {
                      window.location.href = '/checkout?planId=1&planName=Starter&price=49&billingCycle=monthly&interviewCredits=10';
                    }
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  Choose Starter
                </button>
              </div>
            </div>

            {/* Professional Plan */}
            <div className="bg-white border-2 border-purple-200 rounded-lg shadow-sm p-6 hover:shadow-lg transition-shadow relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-purple-600 text-white px-4 py-1 rounded-full text-sm font-medium">Most Popular</span>
              </div>
              <div className="text-center">
                <Star className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Professional</h3>
                <p className="text-gray-600 mt-2 mb-4">Ideal for growing companies</p>
                <div className="text-4xl font-bold text-gray-900 mb-2">$149<span className="text-lg font-medium text-gray-600">/month</span></div>
                <ul className="text-sm text-gray-700 space-y-2 mb-6">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />50 interviews/month</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Advanced AI assessment</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Priority support</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Team collaboration</li>
                </ul>
                <button 
                  onClick={() => {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      window.location.href = `/login?returnUrl=${encodeURIComponent('/checkout?planId=2&planName=Professional&price=149')}`;
                    } else {
                      window.location.href = '/checkout?planId=2&planName=Professional&price=149&billingCycle=monthly&interviewCredits=50';
                    }
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  Choose Professional
                </button>
              </div>
            </div>

            {/* Enterprise Plan */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 hover:shadow-lg transition-shadow">
              <div className="text-center">
                <Crown className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Enterprise</h3>
                <p className="text-gray-600 mt-2 mb-4">For large organizations</p>
                <div className="text-4xl font-bold text-gray-900 mb-2">$399<span className="text-lg font-medium text-gray-600">/month</span></div>
                <ul className="text-sm text-gray-700 space-y-2 mb-6">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />200 interviews/month</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Premium AI assessment</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />24/7 support</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />White-label solution</li>
                </ul>
                <button 
                  onClick={() => {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      window.location.href = `/login?returnUrl=${encodeURIComponent('/checkout?planId=3&planName=Enterprise&price=399')}`;
                    } else {
                      window.location.href = '/checkout?planId=3&planName=Enterprise&price=399&billingCycle=monthly&interviewCredits=200';
                    }
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  Choose Enterprise
                </button>
              </div>
            </div>

            {/* Pay Per Interview */}
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 hover:shadow-lg transition-shadow">
              <div className="text-center">
                <Building2 className="w-12 h-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900">Pay Per Use</h3>
                <p className="text-gray-600 mt-2 mb-4">Flexible pricing</p>
                <div className="text-4xl font-bold text-gray-900 mb-2">$50<span className="text-lg font-medium text-gray-600">/interview</span></div>
                <ul className="text-sm text-gray-700 space-y-2 mb-6">
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />No monthly commitment</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />All premium features</li>
                  <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-500" />Full AI assessment</li>
                </ul>
                <button 
                  onClick={() => {
                    const token = localStorage.getItem('token');
                    if (!token) {
                      window.location.href = `/login?returnUrl=${encodeURIComponent('/checkout?planId=4&planName=PayPerInterview&price=50')}`;
                    } else {
                      window.location.href = '/checkout?planId=4&planName=PayPerInterview&price=50&billingCycle=per_interview&interviewCredits=1';
                    }
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 px-4 rounded-lg font-medium transition-colors"
                >
                  Get Started
                </button>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link to="/pricing" className="text-blue-600 hover:text-blue-700 font-medium">
              View detailed comparison →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Ready to Revolutionize Your Hiring?
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-2xl mx-auto">
            Join hundreds of companies using AI Recruiter to find the best technical talent
          </p>
          <Link to="/login?returnUrl=/interview" className="btn-success text-lg px-8 py-3 inline-flex items-center">
            Start Your First Interview
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <Brain className="w-6 h-6 text-primary-500" />
              <span className="text-lg font-bold text-white">AI Recruiter</span>
            </div>
            <div className="flex space-x-6 text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 AI Recruiter. All rights reserved.</p>
            <p className="mt-2 text-sm text-gray-500">Build {BUILD_VERSION}</p>
          </div>
        </div>
      </footer>
    </div>
  )
}
